import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import BasinSelection from "./BasinSelection";
import GenericCategorySelection from "./GenericCategorySelection";
import { uiActions } from "../../store";
import { API_URL } from "../../config/config.data";

function SearchOptionButton({ icon, title, description, onClick }) {
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 4,
        py: 4,
        width: "250px",
        textTransform: "none",
        borderRadius: 2,
        border: "1px solid rgba(0, 0, 0, 0.2)",
        transition: "transform 0.3s",
        color: (theme) => theme.palette.text.secondary,
        "&:hover": {
          transform: "scale(1.05)",
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
        "&:active": {
          transform: "scale(0.95)",
        },
      }}
    >
      {icon}
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
        }}
      >
        {description}
      </Typography>
    </Button>
  );
}

function countMatchingDatasets(data, filters, basins) {
  let count = 0;

  // need data or filters
  if (!Array.isArray(data) || !filters) {
    return count;
  }

  let filtersActive = false;
  for (let filterCategory in filters) {
    let filterValues = filters[filterCategory];
    if (Array.isArray(filterValues) && filterValues.length > 0) {
      filtersActive = true;
      break;
    }
  }

  for (let i = 0; i < data.length; i++) {
    let dataset = data[i];
    let allFiltersMatch = true; // assuming all filters match initially

    if (!filtersActive) {
      count += 1;
      continue;
    }

    for (let filterCategory in filters) {
      let filterValues = filters[filterCategory];

      const abbreviations = basins?.filter((basin) => filterValues?.includes(basin.name))
        .map((basin) =>
        basin.name.split(/[\s-]+/)
        .map((word) => word[0].toUpperCase())
        .join(""))

      if (!Array.isArray(filterValues) || filterValues.length === 0) {
        continue;
      }

      let match = false;

      if (filterCategory === "ngwos_basin") {
        let spatialExtent = dataset["spatial_extent"];
        if (spatialExtent) {
          let datasetBasins = dataset["project"]
            ? basins.filter((basin) => basin.id === dataset["project"]["basin_id"]).map((basin) => basin.name)
            : spatialExtent.split(/[;,]/).map((s) => s.trim());
          // let datasetBasins = spatialExtent.split(/[;,]/).map((s) => s.trim());
          match = filterValues.some((basin) => 
            datasetBasins.includes(basin)) || abbreviations.some((basin) =>
              datasetBasins.includes(basin));
        }
      } else if (filterCategory === "domain") {
        let datasetDomains = Array.isArray(dataset["domains"])
          ? dataset["domains"].map((d) => d["name"])
          : [];
        match = filterValues.some((domain) => datasetDomains.includes(domain));
      } else if (filterCategory === "variables") {
        let datasetVariables = Array.isArray(dataset["variables"])
          ? dataset["variables"].map((v) => v["name"])
          : [];
        match = filterValues.some((variable) =>
          datasetVariables.includes(variable)
        );
      }

      if (!match) {
        allFiltersMatch = false;
        break;
      }
    }

    if (allFiltersMatch) {
      count += 1;
    }
  }
  return count;
}

export default function DataStepper() {
  const [activeStep, setActiveStep] = useState(0);
  const [isGuided, setIsGuided] = useState(false);
  const [matchingCount, setMatchingCount] = useState(0);
  const [domainOptions, setDomainOptions] = useState([]);
  const [variableOptions, setVariableOptions] = useState([]);
  const open = useSelector((state) => state.ui.showDataStepperModal);
  const dispatch = useDispatch();

  const selectedEntity = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );

  const data = useSelector((state) => state.data[selectedEntity]?.data);
  const basins = useSelector((state) => state.data["basins"]?.data);

  const filters = useSelector(
    (state) => state.dataExplorer.filters["datasets"]
  );

  useEffect(() => {
    const count = countMatchingDatasets(data, filters, basins);
    setMatchingCount(count);
  }, [data, filters]);

  useEffect(() => {
    axios
      .get(`${API_URL}/domains`)
      .then((response) => {
        const options = response.data["domains"].map((item) => item.name);
        setDomainOptions(options);
      })
      .catch((error) => {
        console.error("Error fetching domain options:", error);
      });

    axios
      .get(`${API_URL}/variables`)
      .then((response) => {
        const options = response.data["variables"].map((item) => item.name);
        setVariableOptions(options);
      })
      .catch((error) => {
        console.error("Error fetching variable options:", error);
      });
  }, []);

  const handleClose = () => {
    dispatch(uiActions.setShowDataStepperModal(false));
    setIsGuided(false);
    setActiveStep(0);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleGuidedSearch = () => {
    setIsGuided(true);
  };

  const steps = [
    {
      label: "Select NGWOS Basins",
      content: <BasinSelection />,
    },
    {
      label: "Select Domains of Interest",
      content: (
        <GenericCategorySelection
          categoryId="domain"
          categoryLabel="Domains"
          options={domainOptions}
        />
      ),
    },
    {
      label: "Select Variables of Interest",
      content: (
        <GenericCategorySelection
          categoryId="variables"
          categoryLabel="Variables"
          options={variableOptions}
        />
      ),
    },
  ];

  const maxSteps = steps.length;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ textAlign: "center" }}>
        Welcome to the IWS Basin Data Explorer!
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        {!isGuided ? (
          // welcome modal buttons
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 4,
              mt: 4,
              justifyContent: "center",
            }}
          >
            {/* guided search button */}
            <SearchOptionButton
              icon={<SearchIcon sx={{ fontSize: "3rem", mb: 1 }} />}
              title="Guided Search"
              description="Answer a few questions to narrow down the datasets you're looking for."
              onClick={handleGuidedSearch}
            />

            {/* filter search button */}
            <SearchOptionButton
              icon={<FilterListIcon sx={{ fontSize: "3rem", mb: 1 }} />}
              title="Filter Search"
              description="Use map view and filters to search datasets based on specific criteria."
              onClick={handleClose}
            />
          </Box>
        ) : (
          // guided search steps
          <Box sx={{ width: "100%", maxWidth: "800px", textAlign: "center" }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepIcon-text": {
                  fill: "var(--text-primary)", // inside text color
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: (theme) => theme.palette.secondary.main, // active icon color
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: (theme) => theme.palette.success.main, // completed icon color
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ mt: 4 }}>
              {/* matching count message */}
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  backgroundColor: "#D1E7DD",
                  borderRadius: "8px",
                  border: "1px solid #B6D5C3",
                  padding: "8px 16px",
                }}
              >
                {matchingCount === 0
                  ? "No available products match the selected filtering criteria."
                  : matchingCount === 1
                  ? "1 available product matches the selected filtering criteria."
                  : `${matchingCount} available products match the selected filtering criteria.`}
              </Typography>
              {steps[activeStep].content}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 4,
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="contained"
                color="secondary"
              >
                Back
              </Button>
              {/* skip button */}
              <Button
                onClick={() => {
                  if (activeStep >= 2) {
                    handleClose(); // avoid index error
                  } else {
                    // move to the next step
                    setActiveStep((prevActiveStep) => prevActiveStep + 1);
                  }
                }}
                variant="text"
                color="secondary"
                sx={{ textTransform: "none" }}
              >
                Skip
              </Button>

              {activeStep === maxSteps - 1 ? (
                <Button
                  onClick={handleClose}
                  variant="contained"
                  color="primary"
                >
                  Finish
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  color="secondary"
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
